import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getLocale } from "../../../i18n/config";
import GetData from "../../../services/getData";
import "./patientPayment.css";

const normalizeListResponse = (data) => {
    if (Array.isArray(data?.result)) return data.result;
    if (Array.isArray(data?.logs)) return data.logs;
    if (Array.isArray(data?.data)) return data.data;
    return [];
};

const parseField = (value) => {
    if (value && typeof value === "object" && "value" in value) return value.value;
    return value;
};

const formatDateTime = (rawDate, locale) => {
    const value = parseField(rawDate);
    if (!value) return "—";
    const normalized = String(value).replace(" ", "T");
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString(locale);
};

const formatCell = (value) => {
    const parsed = parseField(value);
    if (parsed == null) return "—";
    const text = String(parsed).trim();
    return text === "" ? "—" : text;
};

export default function PatientHistory({ embedded = false, limit = 0 }) {
    const { t, i18n } = useTranslation("patient");
    const locale = getLocale(i18n.language);
    const { patientId } = useParams();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const loadHistory = async (id = patientId) => {
        setLoading(true);
        setError("");
        try {
            const data = await GetData({
                module: "patient",
                block: "logs",
                action: "list",
                id_patient: id,
            });
            const list = Array.isArray(data?.result) ? data.result : normalizeListResponse(data);
            setLogs(list);
        } catch (err) {
            console.error("Ошибка загрузки истории пациента:", err);
            setLogs([]);
            setError(err?.message || t("history.loadFailed"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadHistory(patientId);
    }, [patientId]);

    if (loading) {
        return <div className="info-text">{t("history.loading")}</div>;
    }

    if (error) {
        return (
            <div>
                <div className="info-text">
                    {t("error", { ns: "common", message: error })}
                </div>
                <button
                    type="button"
                    className="button-default light mt-2"
                    onClick={() => loadHistory()}
                >
                    {t("history.retry")}
                </button>
            </div>
        );
    }

    if (!logs.length) {
        return (
            <div>
                <div className="info-text">{t("history.empty")}</div>
                <button
                    type="button"
                    className="button-default light mt-2"
                    onClick={() => loadHistory()}
                >
                    {t("history.refresh")}
                </button>
            </div>
        );
    }
    return (
        <div className={`patient-payments${embedded ? " patient-history--embedded" : ""}`}>
            <div
                className={embedded ? "table-list" : "page-block table-list"}
                style={{ overflowX: "auto" }}
            >
                <table className="history-table main-table-list rounded-table">
                    <thead>
                        <tr>
                            <th>{t("history.colDate")}</th>
                            <th>{t("history.colUser")}</th>
                            <th>{t("history.colText")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(limit > 0 ? logs.slice(0, limit) : logs).map((log, index) => (
                            <tr key={formatCell(log?.id) !== "—" ? formatCell(log?.id) : `log-${index}`}>
                                <td>{formatDateTime(log?.date, locale)}</td>
                                <td>{formatCell(log?.user_name)}</td>
                                <td>{formatCell(log?.text)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
